import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
	assignClientTalentMatching,
	createTalentSelection,
	deleteTalentSelection,
	excludeClientTalentMatching,
	getAnonymizedTalentPreviewAdmin,
	getMyMatchedTalents,
	getPublicAnonymizedTalent,
	getTalentSelectionDetail,
	listClientTalentMatchingsAdmin,
	listClientTalentMatchingsAdminForClient,
	listMyTalentSelections,
	revokeTalentPublicLink
} from "../api/anonymized-talents-api";

/**
 * React-Query layer for the "Fiche Talent anonymisée" feature.
 * Mirrors the API client one-to-one and centralizes cache keys.
 */

const queryKeys = {
	matchedTalents: ["anonymized-talents", "client-matched"] as const,
	selections: ["anonymized-talents", "selections"] as const,
	selectionDetail: (id: number) =>
		["anonymized-talents", "selections", id] as const,
	publicTalent: (token: string) =>
		["anonymized-talents", "public", token] as const,
	adminMatchings: ["anonymized-talents", "admin-matchings"] as const,
	adminMatchingsForClient: (clientUserId: number) =>
		["anonymized-talents", "admin-matchings", clientUserId] as const,
	adminPreview: (freelancerProfileId: number, lang: string) =>
		["anonymized-talents", "admin-preview", freelancerProfileId, lang] as const
};

// ------------------------------------------------------------
// Option A — Client area
// ------------------------------------------------------------

export const useMyMatchedTalents = (enabled = true) => {
	return useQuery({
		queryKey: queryKeys.matchedTalents,
		queryFn: getMyMatchedTalents,
		enabled,
		staleTime: 5 * 60 * 1000
	});
};

// ------------------------------------------------------------
// Option B — CSM area
// ------------------------------------------------------------

export const useMyTalentSelections = () => {
	return useQuery({
		queryKey: queryKeys.selections,
		queryFn: listMyTalentSelections,
		staleTime: 60 * 1000
	});
};

export const useTalentSelectionDetail = (
	selectionId: number | null | undefined
) => {
	return useQuery({
		queryKey: queryKeys.selectionDetail(selectionId ?? 0),
		queryFn: () => getTalentSelectionDetail(selectionId as number),
		enabled: typeof selectionId === "number" && selectionId > 0,
		staleTime: 30 * 1000
	});
};

export const useCreateTalentSelection = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: createTalentSelection,
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: queryKeys.selections });
		}
	});
};

export const useRevokeTalentPublicLink = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({
			selectionId,
			linkId
		}: {
			selectionId: number;
			linkId: number;
		}) => revokeTalentPublicLink(selectionId, linkId),
		onSuccess: (_data, { selectionId }) => {
			queryClient.invalidateQueries({
				queryKey: queryKeys.selectionDetail(selectionId)
			});
			queryClient.invalidateQueries({ queryKey: queryKeys.selections });
		}
	});
};

export const useDeleteTalentSelection = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: deleteTalentSelection,
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: queryKeys.selections });
		}
	});
};

// ------------------------------------------------------------
// Internal admin audit
// ------------------------------------------------------------

export const useAdminClientTalentMatchings = () => {
	return useQuery({
		queryKey: queryKeys.adminMatchings,
		queryFn: listClientTalentMatchingsAdmin,
		// Audit data is allowed to be a little stale — refreshed manually by the
		// CSM via the page's Reload button when they want to check the latest run.
		staleTime: 60 * 1000
	});
};

/**
 * Per-client variant — fired by the client detail page so the CSM sees the
 * matcher's output inside the file instead of crossing into a dedicated audit
 * page. `enabled` lets the caller defer the fetch until the client id is known.
 */
export const useAdminClientTalentMatchingsForClient = (
	clientUserId: number | null | undefined,
	enabled = true
) => {
	return useQuery({
		queryKey: queryKeys.adminMatchingsForClient(clientUserId ?? 0),
		queryFn: () =>
			listClientTalentMatchingsAdminForClient(clientUserId as number),
		enabled:
			enabled && typeof clientUserId === "number" && clientUserId > 0,
		staleTime: 60 * 1000
	});
};

/**
 * CSM/Admin/TAM action — exclude one talent from a client's live matched batch.
 * On success refreshes both the per-client and the global audit views so the
 * removed talent disappears immediately.
 */
export const useExcludeClientTalentMatching = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({
			matchingId
		}: {
			matchingId: number;
			clientUserId: number;
		}) => excludeClientTalentMatching(matchingId),
		onSuccess: (_data, { clientUserId }) => {
			queryClient.invalidateQueries({
				queryKey: queryKeys.adminMatchingsForClient(clientUserId)
			});
			queryClient.invalidateQueries({ queryKey: queryKeys.adminMatchings });
		}
	});
};

/**
 * CSM/Admin/TAM action — manually add (or re-activate) one talent in a client's
 * live matched batch. Refreshes the per-client and global audit views on success.
 */
export const useAssignClientTalentMatching = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({
			clientUserId,
			freelancerProfileId
		}: {
			clientUserId: number;
			freelancerProfileId: number;
		}) => assignClientTalentMatching(clientUserId, freelancerProfileId),
		onSuccess: (_data, { clientUserId }) => {
			queryClient.invalidateQueries({
				queryKey: queryKeys.adminMatchingsForClient(clientUserId)
			});
			queryClient.invalidateQueries({ queryKey: queryKeys.adminMatchings });
		}
	});
};

/**
 * "Preview as the client sees it" — internal users only. Fed by the matched
 * talent click-through on the client detail page so a CSM can verify the
 * exact anonymized fiche their client renders in-app.
 */
export const useAnonymizedTalentPreviewAdmin = (
	freelancerProfileId: number | null | undefined,
	lang?: "fr" | "en"
) => {
	return useQuery({
		queryKey: queryKeys.adminPreview(freelancerProfileId ?? 0, lang ?? "default"),
		queryFn: () =>
			getAnonymizedTalentPreviewAdmin(freelancerProfileId as number, lang),
		enabled:
			typeof freelancerProfileId === "number" && freelancerProfileId > 0,
		staleTime: 60 * 1000
	});
};

// ------------------------------------------------------------
// Public surface
// ------------------------------------------------------------

export const usePublicAnonymizedTalent = (
	token: string | undefined,
	lang?: "fr" | "en"
) => {
	return useQuery({
		// Cache per (token, lang) so toggling language between FR and EN doesn't
		// re-show the previous-language payload while the new one is in flight.
		queryKey: [...queryKeys.publicTalent(token ?? ""), lang ?? "default"],
		queryFn: () => getPublicAnonymizedTalent(token as string, lang),
		enabled: !!token,
		retry: false,
		staleTime: 5 * 60 * 1000
	});
};
